import { useState } from "react";
import { Link } from "react-router-dom";
import { expeditions } from "@/data/cruises";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { QuoteRequestDialog } from "@/components/common/QuoteRequestDialog";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, Users, Compass, Telescope, Mountain } from "lucide-react";

const USP_ITEMS = [
  { icon: Compass, label: "Zodiac & helicopter landings on every voyage" },
  { icon: Users, label: "Small groups — maximum 199 guests per ship" },
  { icon: Telescope, label: "Expert naturalists, scientists & historians" },
  { icon: Mountain, label: "Places less than 1% of humans will ever see" },
];

const REGION_COLORS = {
  Antarctica: "bg-sky-500/20 text-sky-300 border border-sky-500/30",
  Arctic: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30",
  Exclusive: "bg-amber-500/20 text-amber-300 border border-amber-500/30",
};

export function PolarExpeditionsSection() {
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [selectedExpedition, setSelectedExpedition] = useState(null);
  const featured = expeditions.slice(0, 2);

  const handleQuote = (exp) => {
    setSelectedExpedition({
      type: "cruise",
      cruiseName: exp.title,
      ship: exp.ship,
      duration: exp.duration,
    });
    setQuoteDialogOpen(true);
  };

  return (
    <section className="bg-primary section-padding" data-testid="polar-expeditions-section">
      <div className="container-wide px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <AnimatedSection>
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase text-accent mb-3">Exclusive Access</p>
              <h2 className="heading-lg text-white mb-3">Polar Expeditions</h2>
              <p className="text-white/60 text-sm leading-relaxed max-w-xl">
                Journey to the ends of the Earth. Small-ship expeditions to Antarctica and the Arctic — with expert naturalists, Zodiac landings, and wildlife encounters found nowhere else on the planet.
              </p>
            </div>
            <Link
              to="/cruises"
              className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-accent hover:text-white transition-colors shrink-0"
            >
              View all expeditions <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </AnimatedSection>

        {/* Expedition cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch mb-10">
          {featured.map((exp, i) => (
            <AnimatedSection key={exp.id} delay={i * 0.12} className="h-full">
              <div className="group bg-white rounded-xl overflow-hidden shadow-card-lift h-full flex flex-col">

                {/* Image */}
                <div className="relative overflow-hidden aspect-[16/9] flex-shrink-0">
                  <img
                    src={exp.image}
                    alt={exp.title}
                    className="w-full h-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.2,0,0,1)] group-hover:scale-105"
                    loading="lazy"
                  />
                  {/* Duration badge */}
                  <span className="absolute top-3 left-3 bg-accent text-white text-xs font-semibold px-3 py-1 rounded-full">
                    {exp.duration}
                  </span>
                  {/* Region badge */}
                  <span className={`absolute top-3 right-3 text-xs font-semibold px-3 py-1 rounded-full backdrop-blur-sm ${REGION_COLORS[exp.badge] || REGION_COLORS["Arctic"]}`}>
                    {exp.badge}
                  </span>
                </div>

                {/* Content */}
                <div className="p-5 flex flex-col flex-1">
                  <p className="text-xs text-muted-foreground font-medium mb-2">{exp.ship}</p>
                  <h3 className="text-base font-bold text-primary mb-2 leading-snug group-hover:text-accent transition-colors">
                    {exp.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-2">
                    {exp.description}
                  </p>

                  {/* Highlights */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-4">
                    {exp.highlights.slice(0, 4).map((h, j) => (
                      <span key={j} className="text-xs text-muted-foreground flex items-start gap-1.5">
                        <Check className="h-3 w-3 text-accent shrink-0 mt-0.5" /> {h}
                      </span>
                    ))}
                  </div>

                  {/* Price + CTA */}
                  <div className="flex items-center justify-between mt-auto pt-3.5 border-t border-border/60">
                    <div>
                      <p className="text-xs text-muted-foreground">From</p>
                      <p className="text-xl font-bold text-primary">${exp.startingPrice.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">per person</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleQuote(exp)}
                      className="bg-accent text-white hover:bg-accent/90"
                    >
                      Get Quote <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        {/* USP strip */}
        <AnimatedSection delay={0.25}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {USP_ITEMS.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-start gap-3 bg-white/5 rounded-xl px-4 py-4 border border-white/10">
                <Icon className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                <p className="text-xs text-white/70 leading-snug">{label}</p>
              </div>
            ))}
          </div>
        </AnimatedSection>

        {/* Mobile view all link */}
        <Link
          to="/cruises"
          className="sm:hidden flex items-center justify-center gap-2 text-sm font-semibold text-accent mt-6 hover:text-white transition-colors"
        >
          View all expeditions <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <QuoteRequestDialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen} quoteData={selectedExpedition} />
    </section>
  );
}
